import { useState, useEffect, type FC } from 'react';
import { Select, Button } from 'flowbite-react';
import { HiTrash } from 'react-icons/hi';
import type {
  FilterCondition,
  FilterFieldDefinition,
  FilterFieldOption,
  FilterOperator,
  FilterValue,
} from '../../types/filter';
import {
  HEATMAP_FILTER_FIELDS,
  OPERATOR_LABELS,
  getFilterFieldById,
} from '../../config/heatmap-filter-fields';
import { ValueInput } from './ValueInput';

interface ConditionRowProps {
  condition: FilterCondition;
  isFirst: boolean;
  conjunction: 'and' | 'or';
  onConjunctionChange?: (newConjunction: 'and' | 'or') => void;
  onUpdate: (updated: FilterCondition) => void;
  onDelete: () => void;
  disabled?: boolean;
}

export const ConditionRow: FC<ConditionRowProps> = ({
  condition,
  isFirst,
  conjunction,
  onConjunctionChange,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const currentFieldDef: FilterFieldDefinition =
    getFilterFieldById(condition.field) ?? HEATMAP_FILTER_FIELDS[0]!;
  const [loadedOptions, setLoadedOptions] = useState<FilterFieldOption[]>(
    currentFieldDef.options || []
  );
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Load dynamic options when field definition changes
  useEffect(() => {
    let isMounted = true;
    if (currentFieldDef.loadOptions) {
      setIsLoadingOptions(true);
      currentFieldDef
        .loadOptions()
        .then((opts) => {
          if (isMounted) {
            setLoadedOptions(opts);
            setIsLoadingOptions(false);
          }
        })
        .catch((err) => {
          console.warn('Error loading field options:', err);
          if (isMounted) setIsLoadingOptions(false);
        });
    } else {
      setLoadedOptions(currentFieldDef.options || []);
    }

    return () => {
      isMounted = false;
    };
  }, [currentFieldDef]);

  // Group fields by category for <optgroup>
  const categories = Array.from(
    new Set(HEATMAP_FILTER_FIELDS.map((f) => f.category))
  );

  const handleFieldChange = (newFieldId: string) => {
    const newFieldDef = getFilterFieldById(newFieldId);
    if (!newFieldDef) return;

    const defaultOp = newFieldDef.operators[0] || 'is';
    onUpdate({
      ...condition,
      field: newFieldId,
      operator: defaultOp,
      value: null,
    });
  };

  const handleOperatorChange = (newOp: FilterOperator) => {
    let newValue = condition.value;
    if (newOp === 'between' && (typeof newValue !== 'object' || !newValue || !('min' in newValue))) {
      newValue = { min: 18, max: 65 };
    } else if (newOp === 'is_within' && (typeof newValue !== 'object' || !newValue || !('start' in newValue))) {
      newValue = { start: '', end: '' };
    } else if (newOp === 'is_any_of' || newOp === 'is_none_of') {
      newValue = Array.isArray(newValue) ? newValue : newValue ? [String(newValue)] : [];
    }

    onUpdate({
      ...condition,
      operator: newOp,
      value: newValue,
    });
  };

  const handleValueChange = (newVal: FilterValue) => {
    onUpdate({
      ...condition,
      value: newVal,
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5 transition-colors">
      {/* 1. Conjunction / Prefix */}
      <div className="w-20 shrink-0">
        {isFirst ? (
          <span className="inline-block px-2 text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">
            Where
          </span>
        ) : (
          <Select
            sizing="sm"
            value={conjunction}
            onChange={(e) =>
              onConjunctionChange?.(e.target.value as 'and' | 'or')
            }
            disabled={disabled}
            className="w-full text-xs font-medium"
          >
            <option value="and">and</option>
            <option value="or">or</option>
          </Select>
        )}
      </div>

      {/* 2. Field Selector */}
      <div className="min-w-[160px] shrink-0">
        <Select
          sizing="sm"
          value={condition.field}
          onChange={(e) => handleFieldChange(e.target.value)}
          disabled={disabled}
          className="text-xs"
        >
          {categories.map((cat) => (
            <optgroup key={cat} label={cat}>
              {HEATMAP_FILTER_FIELDS.filter((f) => f.category === cat).map(
                (f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                )
              )}
            </optgroup>
          ))}
        </Select>
      </div>

      {/* 3. Operator Selector */}
      <div className="min-w-[130px] shrink-0">
        <Select
          sizing="sm"
          value={condition.operator}
          onChange={(e) =>
            handleOperatorChange(e.target.value as FilterOperator)
          }
          disabled={disabled}
          className="text-xs"
        >
          {currentFieldDef.operators.map((op) => (
            <option key={op} value={op}>
              {OPERATOR_LABELS[op] || op}
            </option>
          ))}
        </Select>
      </div>

      {/* 4. Value Input */}
      <div className="flex grow items-center">
        <ValueInput
          field={currentFieldDef}
          operator={condition.operator}
          value={condition.value}
          onChange={handleValueChange}
          options={loadedOptions}
          disabled={disabled || isLoadingOptions}
        />
      </div>

      {/* 5. Delete Action Button */}
      <div className="shrink-0">
        <Button
          color="gray"
          size="xs"
          onClick={onDelete}
          disabled={disabled}
          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
          title="Bedingung löschen"
          aria-label="Bedingung löschen"
        >
          <HiTrash className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
